import { Routes, Route, Navigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';

// Layouts
import { AppLayout } from '@/layouts/AppLayout';
import { OutreachLayout } from '@/layouts/OutreachLayout';
import { CampaignLayout } from '@/layouts/CampaignLayout';
import { PublicAcademyLayout } from '@/layouts/PublicAcademyLayout';

// Public pages
import { LandingPage } from '@/pages/LandingPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { TermsPage } from '@/pages/TermsPage';
import { ImprintPage } from '@/pages/ImprintPage';
import { CookiePolicyPage } from '@/pages/CookiePolicyPage';
import { LoginPage } from '@/pages/LoginPage';
import { AuthCallback } from '@/pages/AuthCallback';
import { OAuthConsentPage } from '@/pages/OAuthConsentPage';

// Protected pages
import { WorkspacePage } from '@/pages/DashboardPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { OnboardingReadyPage } from '@/pages/OnboardingReadyPage';
import { LeadsPage } from '@/pages/LeadsPage';
import { DraftEmailsPage } from '@/pages/DraftEmailsPage';
import { ReviewSendPage } from '@/pages/ReviewSendPage';
import { SettingsGeneralPage } from '@/pages/SettingsGeneralPage';
import { SettingsUsagePage } from '@/pages/SettingsUsagePage';
import { BillingPage } from '@/pages/BillingPage';
import { PricingConfigPage } from '@/pages/PricingConfigPage';
import { ApiKeysPage } from '@/pages/ApiKeysPage';
import { CampaignRedirect } from '@/pages/CampaignRedirect';
import { CampaignSetupPage } from '@/pages/CampaignSetupPage';
import { CampaignLeadsPage } from '@/pages/CampaignLeadsPage';
import { CampaignReviewPage } from '@/pages/CampaignReviewPage';
import { AcademyGuidePage } from '@/pages/AcademyGuidePage';
import { AcademyApiReferencePage } from '@/pages/AcademyApiReferencePage';
import { AcademyMcpPage } from '@/pages/AcademyMcpPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

function ProtectedRoute({ children, allowOnboarding }: { children: React.ReactNode; allowOnboarding?: boolean }) {
  const { isAuthenticated, loading, needsOnboarding } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // First-time users → onboarding wizard (unless we're already on an onboarding page)
  if (needsOnboarding && !allowOnboarding) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/imprint" element={<ImprintPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />

      {/* Public Academy (no login required) */}
      <Route element={<PublicAcademyLayout />}>
        <Route path="/docs/guide" element={<AcademyGuidePage />} />
        <Route path="/docs/mcp" element={<AcademyMcpPage />} />
        <Route path="/docs/api-reference" element={<AcademyApiReferencePage />} />
      </Route>

      {/* Onboarding (protected) */}
      <Route path="/onboarding" element={
        <ProtectedRoute allowOnboarding><OnboardingPage /></ProtectedRoute>
      } />
      <Route path="/onboarding/ready" element={
        <ProtectedRoute allowOnboarding><OnboardingReadyPage /></ProtectedRoute>
      } />

      {/* App routes (protected, with sidebar layout) */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<WorkspacePage />} />
        <Route path="/settings" element={<SettingsGeneralPage />} />
        <Route path="/settings/usage" element={<SettingsUsagePage />} />
        <Route path="/settings/billing" element={<BillingPage />} />
        <Route path="/settings/api-keys" element={<ApiKeysPage />} />

        {/* HyPeMa Academy (in-app) */}
        <Route path="/academy/guide" element={<AcademyGuidePage />} />
        <Route path="/academy/mcp" element={<AcademyMcpPage />} />
        <Route path="/academy/api-reference" element={<AcademyApiReferencePage />} />

        {/* Control Center (admin-only) */}
        <Route path="/control-center/pricing" element={<PricingConfigPage />} />

        {/* Outreach sub-routes */}
        <Route element={<OutreachLayout />}>
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/draft-emails" element={<DraftEmailsPage />} />
          <Route path="/review-send" element={<ReviewSendPage />} />
        </Route>

        {/* Campaign sub-routes */}
        <Route element={<CampaignLayout />}>
          <Route path="/campaign" element={<CampaignRedirect />} />
          <Route path="/campaign/:id/setup" element={<CampaignSetupPage />} />
          <Route path="/campaign/:id/leads" element={<CampaignLeadsPage />} />
          <Route path="/campaign/:id/review" element={<CampaignReviewPage />} />
        </Route>
      </Route>

      {/* Catch-all: redirect to landing */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
